import { useState } from 'react';
import { RESTOCKING_HINT } from './pricing';

type Props = {
  /** Raw per-operator availability from the backend. */
  availability: string;
  onChange: (availability: 'enabled' | 'staged') => Promise<void>;
};

/**
 * Publish state of one pack for this operator.
 *
 * `staged` packs are the ones being prepared: they show up here (and can be
 * dressed with assets, badge, description, pricing) but stay out of the player
 * catalog and cannot be bought until published. `restocking` is machine state
 * and is shown read-only: the backend refuses every console-driven move out of
 * it, because unpublishing then republishing would put a stocked-out pack back
 * on sale. Auto-recovery owns that edge.
 */
export function PublishControl({ availability, onChange }: Props) {
  const [isBusy, setIsBusy] = useState(false);

  const run = async (next: 'enabled' | 'staged') => {
    setIsBusy(true);
    try {
      await onChange(next);
    } finally {
      setIsBusy(false);
    }
  };

  if (availability === 'staged') {
    return (
      <div className="flex shrink-0 items-center gap-1.5">
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
          Not published
        </span>
        <button
          type="button"
          onClick={() => void run('enabled')}
          disabled={isBusy}
          title="Show this pack to players"
          className="rounded bg-[var(--co-brand)] px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50"
        >
          Publish
        </button>
      </div>
    );
  }

  if (availability === 'restocking') {
    return (
      <span
        title={RESTOCKING_HINT}
        className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-800"
      >
        Restocking
      </span>
    );
  }

  if (availability === 'enabled') {
    return (
      <div className="flex shrink-0 items-center gap-1.5">
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
          Live
        </span>
        <button
          type="button"
          onClick={() => void run('staged')}
          disabled={isBusy}
          title="Hide this pack from players while you work on it"
          className="rounded border border-[var(--co-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--co-muted)] transition-colors hover:text-[var(--co-text)] disabled:opacity-50"
        >
          Unpublish
        </button>
      </div>
    );
  }

  return null;
}
